import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCompanyContext } from 'src/companies/routes/company-context'
import {
  FindOrCreateTenantConfiguration,
  GetConfigurables,
  UpdateTenantConfiguration,
} from 'src/api/microservices/tenant-config'

export const TENANT_CONFIG_QUERY_KEY = 'tenant-config'
export const TENANT_CONFIG_CONFIGURATION_QUERY_KEY = 'tenant-config-slug'
export const TENANT_CONFIG_UPDATE_MUTATION_KEY = 'tenant-config-update'

export type UpdateTenantConfigurationRequest = {
  configurations: KeyValuePair[]
}

export function useGetTenantConfigurables(baseUri: string) {
  const { company } = useCompanyContext()
  return useQuery({
    queryKey: [TENANT_CONFIG_QUERY_KEY, { baseUri }],
    queryFn: async () => {
      let [err, response] = await GetConfigurables(baseUri, company.tenant_id, company.slug)
      if (err) throw err
      return response.configurables
    },
  })
}

export function useFindOrCreateTenantConfiguration(baseUri: string) {
  const { company } = useCompanyContext()
  return useQuery({
    queryKey: [TENANT_CONFIG_CONFIGURATION_QUERY_KEY, { baseUri }],
    queryFn: async () => {
      const [err, data] = await FindOrCreateTenantConfiguration(
        baseUri,
        company.tenant_id,
        company.slug
      )
      if (err) throw err
      return data.configurations
    },
  })
}

export function useUpdateTenantConfiguration(baseUri: string) {
  const { company } = useCompanyContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [TENANT_CONFIG_UPDATE_MUTATION_KEY, { baseUri }],
    mutationFn: async (config: UpdateTenantConfigurationRequest) => {
      const [err, response] = await UpdateTenantConfiguration(
        baseUri,
        config,
        company.tenant_id,
        company.slug
      )
      if (err) throw err

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [TENANT_CONFIG_QUERY_KEY, { baseUri }] }),
        queryClient.invalidateQueries({
          queryKey: [TENANT_CONFIG_CONFIGURATION_QUERY_KEY, { baseUri }],
        }),
      ])

      return response
    },
  })
}
